import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

export type Lang = 'da' | 'en';

// Visitor language is a PUBLIC-surface preference — persisted only in the
// browser, never to the backend (AC2). Written ONLY on an explicit user switch;
// browser-detection defaults are never persisted.
const STORAGE_KEY = 'locale';

// The languages emjo-login can always render — its bundled da/en translation
// JSON ships regardless of operator overrides (Story 29.4 interpretation note).
const BUNDLED_LANGS = ['da', 'en'];

/**
 * LanguageService — language detection + persistence for the public SPAs
 * (brand-site, emjo-login). Story 25.5.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly transloco = inject(TranslocoService);

  /**
   * The language to start in: a previously-stored explicit choice wins;
   * otherwise browser detection — `da` when `navigator.language` starts with
   * `da`, else `en` (AC2). Detection does NOT write to storage.
   */
  detect(): Lang {
    const stored = this.readStored();
    if (stored) {
      return stored;
    }
    const nav = (navigator.language ?? '').toLowerCase();
    return nav.startsWith('da') ? 'da' : 'en';
  }

  /** The currently active language. */
  current(): Lang {
    const active = this.transloco.getActiveLang();
    return active === 'da' ? 'da' : 'en';
  }

  /** Switch the active language AND persist it — explicit user action only. */
  set(lang: Lang): void {
    this.transloco.setActiveLang(lang);
    this.write(lang);
  }

  /**
   * Story 29.4 — apply an Environment operator's default/fallback language.
   *
   * Slots between "explicit stored choice" and "browser fallback": it activates
   * `defaultLang` ONLY when the visitor has no stored choice AND their browser's
   * preferred language is not one the login surface can render — i.e. not in the
   * bundled da/en ∪ the operator's override languages (`overrideLangs`, the
   * keys of `i18n_overrides`). The browser language always wins when available;
   * a stored explicit choice always wins over this default.
   *
   * NEVER persists (mirrors detection — only an explicit `set()` writes to
   * storage). Takes/applies a raw `string` so any operator-configured code is
   * honoured without widening the persisted `Lang` union.
   */
  applyEnvironmentDefault(defaultLang: string | null | undefined, overrideLangs: string[] = []): void {
    if (!defaultLang) {
      return;
    }
    if (this.readStored()) {
      return; // an explicit stored choice wins over the environment default
    }
    const available = new Set<string>([...BUNDLED_LANGS, ...overrideLangs]);
    const browserPrimary = (navigator.language ?? '').toLowerCase().split('-')[0];
    if (available.has(browserPrimary)) {
      return; // the browser's language is renderable → it wins, leave detect()'s result
    }
    // Browser language unavailable + no stored choice → fall back to the
    // operator's default, but only if the default itself is renderable (guards
    // against a stale backend value after the operator removed that language
    // from the I18n map). Not persisted (a later explicit switch still wins).
    if (!available.has(defaultLang)) {
      return;
    }
    this.transloco.setActiveLang(defaultLang);
  }

  private readStored(): Lang | null {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return v === 'da' || v === 'en' ? v : null;
    } catch {
      return null;
    }
  }

  private write(lang: Lang): void {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // best-effort browser persistence; non-fatal
    }
  }
}
